import { zodResolver } from '@hookform/resolvers/zod';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import MuiFormInput from 'components/Form/MuiFormInput/MuiFormInput';
import MuiFormSelect from 'components/Form/MuiFormSelect/MuiFormSelect';
import Popup from 'components/Popup/Popup';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ModalSizeE } from 'declarations/enums/modal';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import type { NoopT } from 'declarations/types/common';

import {
  FlexContainerControlsStyled,
  FormStyled,
  getPopupContentStyle,
} from './AddAttachmentPopup.styled';
import type { TicketAttachmentFormValuesT } from './declarations';
import useAddAttachmentPopup from './hooks/useAddAttachmentPopup';
import validationSchema from './validationSchema';

export type AddAttachmentPopupPropsT = {
  ticketId: string;
  categoryName: string;
  isOpen: boolean;
  assignToWarrCloud: boolean;
  onClose: NoopT;
};

const AddAttachmentPopup: FC<AddAttachmentPopupPropsT> = ({
  ticketId,
  categoryName,
  isOpen,
  assignToWarrCloud,
  onClose,
}) => {
  const { isMobile } = useResponsive();
  const {
    handleSubmitForm,
    isCreatingAttachment,
    handleClosePopup,
    isLoadingAttachmentTypesOptions,
    attachmentTypesOptions,
  } = useAddAttachmentPopup({ ticketId, categoryName, assignToWarrCloud });

  const fromMethods = useForm<TicketAttachmentFormValuesT>({
    defaultValues: {
      note: '',
      attachmentTypeCode: '',
    },
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = fromMethods;

  const attachmentFileValue = useWatch<TicketAttachmentFormValuesT, 'attachmentFile'>({
    control,
    name: 'attachmentFile',
  });
  const attachmentPhotoValue = useWatch<TicketAttachmentFormValuesT, 'attachmentPhoto'>({
    control,
    name: 'attachmentPhoto',
  });

  const attachmentFileNames = attachmentFileValue
    ? Array.from(attachmentFileValue)
        .map(file => file.name)
        .join(', ')
    : '';
  const attachmentPhotoNames = attachmentPhotoValue
    ? Array.from(attachmentPhotoValue)
        .map(file => file.name)
        .join(', ')
    : '';
  const attachmentNames = [attachmentFileNames, attachmentPhotoNames]
    .filter(name => name !== '')
    .join(',');

  useEffect(
    () => () => {
      if (isOpen) {
        reset();
      }
    },
    [isOpen, reset]
  );

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      let fileList: FileList;
      if (typeof DataTransfer !== 'undefined') {
        const dt = new DataTransfer();
        acceptedFiles.forEach(file => dt.items.add(file));
        fileList = dt.files;
      } else {
        const arr: File[] & { item?: (i: number) => File | null } = [...acceptedFiles];
        arr.item = (i: number): File | null => arr[i] ?? null;
        fileList = arr as unknown as FileList;
      }

      fromMethods.setValue('attachmentFile', fileList, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [fromMethods]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    multiple: true,
    noClick: false,
  });

  const attachmentTypesMuiOptions = attachmentTypesOptions.map(o => ({
    value: o.value,
    label: o.label,
  }));

  return (
    <Popup
      popupId={ScrollLockIdE.AddAttachmentPopup}
      modalTitle="Add Attachment"
      contentStyle={getPopupContentStyle(isMobile)}
      size={ModalSizeE.ADD_ATTACHMENT}
      isOpen={isOpen}
      onClose={handleClosePopup(onClose)}
      showCloseBtn
      closeOnEscape={false}
      validationErrorCount={Object.keys(errors).length}
    >
      <FormProvider {...fromMethods}>
        <FormStyled
          role="form"
          onSubmit={handleSubmit((data): void => {
            handleSubmitForm(data, onClose);
          })}
        >
          <Box
            {...getRootProps()}
            data-testid="add-attachment-dropzone"
            sx={{
              mb: 2,
              p: 2,
              border: '2px dashed',
              borderColor: ((): string => {
                if (isDragActive) return 'primary.main';
                if (attachmentNames) return 'success.main';
                return 'divider';
              })(),
              borderRadius: 1,
              bgcolor: isDragActive ? 'action.hover' : 'transparent',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'border-color 0.2s, background-color 0.2s',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            <input {...getInputProps()} data-testid="add-attachment-file-input" />
            <CloudUploadOutlinedIcon
              sx={{ fontSize: 32, color: attachmentNames ? 'success.main' : 'action.active' }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {isDragActive ? 'Drop files here' : 'Drag & drop files here, or click to browse'}
            </Typography>
            {attachmentNames && (
              <Typography
                variant="caption"
                color="success.main"
                sx={{ mt: 0.5, display: 'block', wordBreak: 'break-all' }}
              >
                {attachmentNames}
              </Typography>
            )}
          </Box>

          {isMobile && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="label"
                htmlFor="add-attachment-photo-input"
                sx={{ cursor: 'pointer' }}
              >
                <input
                  {...fromMethods.register('attachmentPhoto')}
                  type="file"
                  capture="user"
                  accept="image/*"
                  multiple
                  id="add-attachment-photo-input"
                  data-testid="add-attachment-photo-input"
                  style={{ display: 'none' }}
                />
                <Tooltip title="Camera" arrow>
                  <IconButton
                    component="span"
                    aria-label="Camera"
                    sx={{
                      color: attachmentPhotoNames
                        ? (t): string => t.palette.success.main
                        : (t): string => t.palette.primary.main,
                    }}
                  >
                    <PhotoCameraIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  minWidth: 0,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {attachmentPhotoNames || 'Photo not selected'}
              </Typography>
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            <MuiFormInput name="note" label="Attachment Note" placeholder="Attachment Note" />
          </Box>

          <Box sx={{ mb: 2 }} data-testid="add-attachment-type-select">
            <MuiFormSelect
              name="attachmentTypeCode"
              label="Type"
              placeholder=""
              options={attachmentTypesMuiOptions}
              isLoading={isLoadingAttachmentTypesOptions}
              required
            />
          </Box>

          {!isValid && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              {errors.attachmentFile?.message || errors.attachmentPhoto?.message}
            </Typography>
          )}
          <FlexContainerControlsStyled justify={isMobile ? 'center' : 'flex-end'}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isCreatingAttachment}
              startIcon={
                isCreatingAttachment ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              Publish
            </Button>
          </FlexContainerControlsStyled>
        </FormStyled>
      </FormProvider>
    </Popup>
  );
};

export default AddAttachmentPopup;
